import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCategory } from '../hooks/useCategories';
import { useFavorites } from '../hooks/useFavorites';
import { Event, eventService } from '../services/eventService';

interface EventCardProps {
  event: Event;
  showFavoriteButton?: boolean;
  onPress?: () => void;
  className?: string;
  onDelete?: () => void;
}

export function EventCard({
  event,
  showFavoriteButton = true,
  onPress,
  className = "",
  onDelete
}: EventCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { category } = useCategory(event.category || '');
  const { toggleFavorite, isFavorited, loading: favoriteLoading } = useFavorites();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleCreatorPress = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (event.createdBy) {
      navigate(`/users/${event.createdBy}`);
    }
  };

  const handleCardClick = () => {
    if (onPress) {
      onPress();
    } else {
      navigate(`/events/${event.id}`);
    }
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (event.id) {
      toggleFavorite(event.id);
    }
  };

  const handleFavoriteTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Prevents the synthetic click event
    if (event.id) {
      toggleFavorite(event.id);
    }
  };

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMenu(!showMenu);
  };

  const handleMenuTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMenu(!showMenu);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowShareModal(true);
    setCopied(false);
  };

  const handleCopyLink = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/events/${event.id}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      alert('Failed to copy link');
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    navigate(`/events/${event.id}/edit`);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!event.id) return;

    try {
      setDeleting(true);
      await eventService.deleteEvent(event.id);
      setShowDeleteConfirm(false);
      if (onDelete) {
        onDelete();
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event. Please try again.');
      setDeleting(false);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const formatPrice = () => {
    if (event.isFree) return 'Free';
    if (typeof event.cost === 'number') return `$${event.cost}`;
    return `$${event.cost}` || 'Free';
  };

  return (
    <div 
      className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02] relative group ${className}`}
      onClick={handleCardClick}
    >
      {/* Desktop Layout */}
      <div className="hidden md:flex flex-col w-full h-full min-h-[320px]">
        {/* Image Container */}
        <div className="relative w-full h-48 flex-shrink-0">
          <img
            src={event.image || event.linkPreview?.image || 'https://via.placeholder.com/256x144/f3f4f6/64748b?text=Event'}
            alt={event.title}
            className="absolute inset-0 w-full h-full object-cover bg-gray-200"
            referrerPolicy="no-referrer"
          />
          
          {/* Buttons - Desktop */}
          <div className="absolute top-3 right-3 flex items-center gap-2 z-20">
            {showFavoriteButton && user && (
              <button
                onClick={handleFavoriteClick}
                disabled={favoriteLoading}
                className="p-2 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200"
                title={isFavorited(event.id!) ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg
                  className={`w-4 h-4 transition-colors ${
                    isFavorited(event.id!) ? 'text-red-500 fill-current' : 'text-gray-400'
                  }`}
                  fill={isFavorited(event.id!) ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            )}

            {/* More Options Menu */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={handleMenuClick}
                className="p-2 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200"
                title="More options"
              >
                <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                </svg>
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    onClick={handleShare}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share
                  </button>

                  {user && user.uid === event.createdBy && (
                    <>
                      <button
                        onClick={handleEdit}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={handleDeleteClick}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Category chip - Desktop only */}
          {category && (
            <div 
              className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium text-white z-10"
              style={{ backgroundColor: category.color || '#3b82f6' }}
            >
              {category.icon && <span className="text-xs">{category.icon}</span>}
              <span>{category.name}</span>
            </div>
          )}

          {/* Price - Desktop only (absolute position) */}
          {!event.isFree && (
            <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-lg z-10">
              <span className="text-primary-600 font-bold text-sm">{formatPrice()}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="font-semibold text-lg text-gray-900 mb-1 line-clamp-2">
            {event.title}
          </h3>
          <p className="text-sm text-gray-600 mb-3 flex-1 line-clamp-5">
            {event.description}
          </p>

          {/* Date and Time */}
          <div className="mb-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>
                {event.startDate && event.endDate && event.startDate !== event.endDate
                  ? `${new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(event.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                  : event.startDate
                    ? new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : event.date || 'Date TBD'
                }
              </span>
            </div>
            {event.startTime && event.endTime && (
              <div className="text-xs text-gray-500 mt-1 ml-5">
                {event.startTime} - {event.endTime}
              </div>
            )}
          </div>

          {/* Creator */}
          {event.createdByName && (
            <button
              onClick={handleCreatorPress}
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 transition-colors mt-auto"
            >
              {event.createdByAvatar ? (
                <img
                  src={event.createdByAvatar}
                  alt={event.createdByName}
                  className="w-5 h-5 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <span className="truncate">by {event.createdByName}</span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden flex flex-col w-full">
        {/* Image Container */}
        <div className="relative w-full aspect-[16/9]">
          <img
            src={event.image || event.linkPreview?.image || 'https://via.placeholder.com/320x180/f3f4f6/64748b?text=Event'}
            alt={event.title}
            className="w-full h-full object-cover rounded-t-2xl bg-gray-200"
            referrerPolicy="no-referrer"
          />

          {/* Category Badge - Top Left */}
          {category && (
            <div
              className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-white shadow-sm"
              style={{ backgroundColor: category.color || '#3b82f6' }}
            >
              {category.icon && <span>{category.icon}</span>}
              <span>{category.name}</span>
            </div>
          )}

          {/* Price Badge - Top Right */}
          {!event.isFree && (
            <div className="absolute top-2 right-2 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-lg shadow-sm">
              <span className="text-primary-700 font-bold text-sm">{formatPrice()}</span>
            </div>
          )}

          {/* Action Buttons - Bottom Right */}
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5 z-20">
            {showFavoriteButton && user && (
              <button
                onClick={handleFavoriteClick}
                onTouchEnd={handleFavoriteTouchEnd}
                disabled={favoriteLoading}
                className="p-2 rounded-full bg-white/95 backdrop-blur-sm shadow-sm active:bg-white transition-all duration-200 touch-manipulation"
                title={isFavorited(event.id!) ? 'Remove from favorites' : 'Add to favorites'}
              >
                <svg
                  className={`w-5 h-5 transition-colors ${
                    isFavorited(event.id!) ? 'text-red-500 fill-current' : 'text-gray-500'
                  }`}
                  fill={isFavorited(event.id!) ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
            )}

            <button
              onClick={handleMenuClick}
              onTouchEnd={handleMenuTouchEnd}
              className="p-2 rounded-full bg-white/95 backdrop-blur-sm shadow-sm active:bg-white transition-all duration-200 touch-manipulation"
              title="More options"
            >
              <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          {/* Title */}
          <h3 className="font-semibold text-base text-gray-900 mb-2 line-clamp-2 leading-tight">
            {event.title}
          </h3>

          {/* Date and Time */}
          <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-2">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>
              {event.startDate && event.endDate && event.startDate !== event.endDate
                ? `${new Date(event.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(event.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : event.startDate
                  ? new Date(event.startDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  : event.date || 'Date TBD'
              }
              {event.startTime && event.endTime && (
                <span className="text-gray-400"> · {event.startTime} - {event.endTime}</span>
              )}
            </span>
          </div>

          {/* Creator */}
          {event.createdByName && (
            <button
              onClick={handleCreatorPress}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {event.createdByAvatar ? (
                <img
                  src={event.createdByAvatar}
                  alt={event.createdByName}
                  className="w-5 h-5 rounded-full object-cover"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center">
                  <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <span>by {event.createdByName}</span>
            </button>
          )}
        </div>
      </div>

      {/* Share Modal - Rendered via Portal */}
      {showShareModal && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={(e) => {
            e.stopPropagation();
            setShowShareModal(false);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Share Event</h3>
                <p className="text-gray-600 text-sm">
                  Share this event with others
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Link</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/events/${event.id}`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    (e.target as HTMLInputElement).select();
                  }}
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowShareModal(false);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleCopyLink}
                className="px-4 py-2 text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Link</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal - Rendered via Portal */}
      {showDeleteConfirm && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(false);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.082 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Delete Event</h3>
                <p className="text-gray-600 text-sm">
                  Are you sure you want to delete "{event.title}"? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(false);
                }}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  'Delete Event'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}